import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, FileText, Loader2, RotateCcw, ShieldCheck, Trash2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePortalFocus, useScrollToFocus } from "@/hooks/usePortalFocus";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  addCertifier,
  decideCertification,
  formatWhen,
  listCertifications,
  listCertifiers,
  removeCertifier,
} from "@/lib/certification";

const FIRMS = [
  { id: "lacw", name: "Law and Advocacy Centre for Women" },
  { id: "eas", name: "EAS Legal" },
  { id: "vals", name: "VALS" },
  { id: "jbl", name: "Jarrod Behan Law" },
  { id: "rga", name: "RGA" },
];

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  certified: "bg-green-50 text-green-800 border-green-200",
  returned: "bg-red-50 text-red-800 border-red-200",
  withdrawn: "bg-slate-50 text-slate-500 border-slate-200",
};
const STATUS_LABELS = { pending: "Waiting", certified: "Certified", returned: "Returned", withdrawn: "Replaced" };

/**
 * Legal aid applications sent for senior certification. A firm's certifiers certify or
 * return them here; anyone else sees what they've sent. With no `brand` (the administrator
 * page) a firm is chosen, and the firm's certifiers can be set.
 */
export default function CertificationReview({ brand: fixedBrand }) {
  // An application opened from the morning summary brings its firm with it.
  const focus = usePortalFocus("certification", fixedBrand);
  const [brand, setBrand] = useState(fixedBrand || focus?.brand || "lacw");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await listCertifications(brand));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [brand]);

  useEffect(() => { load(); }, [load]);

  const viewer = data?.viewer || {};
  const rows = data?.certifications || [];
  const waiting = rows.filter((r) => r.status === "pending");
  const done = rows.filter((r) => r.status !== "pending" && r.status !== "withdrawn").slice(0, 50);
  useScrollToFocus(focus?.id, Boolean(data) && !loading);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-slate-600" /> Certifications
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Legal aid applications must be certified by a senior member of the firm before they are lodged.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!fixedBrand && (
            <select
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
              aria-label="Firm"
            >
              {FIRMS.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
          </Button>
        </div>
      </div>

      {error && <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-800">{error}</div>}

      {viewer.isAdmin && <CertifierSettings brand={brand} onChange={load} />}

      {data && !data.required && (
        <div className="p-4 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-600">
          Certification isn't switched on for this firm yet. It starts once the firm's certifiers are added
          {viewer.isAdmin ? " above" : " by Complete Law Support"}; until then applications can be lodged as before.
        </div>
      )}

      {data && data.required && !viewer.isCertifier && !viewer.isAdmin && (
        <div className="p-4 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-600">
          Applications are certified by {data.certifiers.map((c) => c.name || c.email).join(" or ")}. Below is what you've sent.
        </div>
      )}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-slate-700">Waiting for certification ({waiting.length})</h2>
        {waiting.length === 0 && !loading && <p className="text-sm text-slate-400">Nothing waiting.</p>}
        {waiting.map((row) => (
          <CertificationCard key={row.id} row={row} viewer={viewer} onDecided={load} />
        ))}
      </section>

      {done.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-700">Recently decided</h2>
          {done.map((row) => (
            <CertificationCard key={row.id} row={row} viewer={viewer} onDecided={load} />
          ))}
        </section>
      )}
    </div>
  );
}

function CertificationCard({ row, viewer, onDecided }) {
  const [returning, setReturning] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const ownSubmission = viewer.email && viewer.email === String(row.submitted_by_email || "").toLowerCase();
  const canDecide = row.status === "pending" && viewer.isCertifier && !ownSubmission;

  const decide = async (decision) => {
    setBusy(true);
    try {
      const result = await decideCertification(row.id, decision, note);
      toast.success(decision === "certified" ? "Certified. The lawyer has been told it can be lodged." : "Returned to the lawyer with your note.");
      if (result.emailed === false) toast.warning("The email to the lawyer couldn't be sent. They'll still see it in the portal.");
      onDecided();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-focus-id={row.id} className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-slate-900">{row.client_name || "Unnamed client"}</p>
          <p className="text-xs text-slate-500">
            {[row.file_number && `File ${row.file_number}`, row.guideline].filter(Boolean).join(" · ")}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Sent by {row.submitted_by || row.submitted_by_email} on {formatWhen(row.submitted_at)}
          </p>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${STATUS_STYLES[row.status] || ""}`}>
          {STATUS_LABELS[row.status] || row.status}
        </span>
      </div>

      {row.submission_note && (
        <p className="text-sm text-slate-700 bg-slate-50 rounded-md p-2 whitespace-pre-wrap">{row.submission_note}</p>
      )}

      <ul className="flex flex-wrap gap-2">
        {(row.documents || []).map((doc, i) => (
          <li key={i}>
            <a
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-700 hover:bg-slate-50"
            >
              <FileText className="w-3.5 h-3.5 text-slate-400" /> {doc.label || doc.name}
            </a>
          </li>
        ))}
      </ul>

      {row.status !== "pending" && row.decided_at && (
        <p className="text-xs text-slate-500">
          {STATUS_LABELS[row.status]} by {row.decided_by || row.decided_by_email} on {formatWhen(row.decided_at)}
          {row.decision_note ? ` – ${row.decision_note}` : ""}
        </p>
      )}

      {row.lodge_requested_at && (
        <p className="text-xs text-green-700">
          Sent to Complete Law Support to lodge by {row.lodge_requested_by || row.lodge_requested_by_email} on {formatWhen(row.lodge_requested_at)}
        </p>
      )}

      {row.status === "pending" && viewer.isCertifier && ownSubmission && (
        <p className="text-xs text-slate-500">You sent this, so another certifier needs to certify it.</p>
      )}

      {canDecide && !returning && (
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" className="gap-1.5 bg-green-700 hover:bg-green-800" disabled={busy} onClick={() => decide("certified")}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Certified – OK to lodge
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={() => setReturning(true)}>
            <RotateCcw className="w-4 h-4" /> Return to lawyer
          </Button>
        </div>
      )}

      {canDecide && returning && (
        <div className="space-y-2 pt-1">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What needs changing before it can be certified?"
            rows={3}
            autoFocus
          />
          <div className="flex gap-2">
            <Button size="sm" variant="destructive" disabled={busy || !note.trim()} onClick={() => decide("returned")}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Return with this note"}
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setReturning(false); setNote(""); }}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CertifierSettings({ brand, onChange }) {
  const [certifiers, setCertifiers] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setCertifiers(await listCertifiers(brand));
    } catch (err) {
      toast.error(err.message);
    }
  }, [brand]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await addCertifier(brand, email, name);
      setEmail("");
      setName("");
      await load();
      onChange();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (certifier) => {
    if (!window.confirm(`Remove ${certifier.name || certifier.email} as a certifier?`)) return;
    try {
      await removeCertifier(certifier.id);
      await load();
      onChange();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-slate-700">Certifiers for this firm</h2>
        <p className="text-xs text-slate-500 mt-0.5">
          Adding the first certifier switches certification on: the firm's applications can't be lodged until one of
          these people certifies them. Each needs a portal account for this firm to certify.
        </p>
      </div>
      {certifiers.length === 0 && <p className="text-sm text-slate-400">No certifiers yet.</p>}
      <ul className="divide-y divide-slate-100">
        {certifiers.map((c) => (
          <li key={c.id} className="flex items-center justify-between py-2 text-sm">
            <span>{c.name || c.email} <span className="text-slate-400">{c.name ? c.email : ""}</span></span>
            <Button variant="ghost" size="sm" onClick={() => remove(c)} aria-label={`Remove ${c.email}`}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </li>
        ))}
      </ul>
      <form onSubmit={add} className="flex flex-wrap gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="w-48" />
        <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" type="email" required className="w-64" />
        <Button type="submit" size="sm" disabled={busy || !email} className="gap-1.5">
          <UserPlus className="w-4 h-4" /> Add certifier
        </Button>
      </form>
    </section>
  );
}
